import type { Session } from '@supabase/supabase-js';

function isInvalidAuthSessionError(error: unknown): boolean {
  const status = (error as { status?: unknown })?.status;
  if (status === 401 || status === 403) return true;

  const message = String((error as { message?: unknown })?.message ?? '').toLowerCase();
  return (
    message.includes('invalid jwt') ||
    message.includes('jwt expired') ||
    message.includes('session not found') ||
    message.includes('user from sub claim in jwt does not exist')
  );
}

async function clearLocalAuthSession(
  supabase: (typeof import('../supabase'))['supabase']
): Promise<void> {
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch {
    // Best effort: callers should treat the session as absent even if cleanup fails.
  }
}

export async function getVerifiedSession(): Promise<Session | null> {
  const { supabase } = await import('../supabase');
  const {
    data: { session },
    error,
  } = await supabase.auth.getSession();

  if (error) {
    if (isInvalidAuthSessionError(error)) {
      await clearLocalAuthSession(supabase);
      return null;
    }
    throw error;
  }

  if (!session?.user) {
    return null;
  }

  const { data, error: userError } = await supabase.auth.getUser(session.access_token);
  if (userError || !data.user) {
    if (!userError || isInvalidAuthSessionError(userError)) {
      await clearLocalAuthSession(supabase);
      return null;
    }
    throw userError;
  }

  return session;
}
